import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { DashboardConfiguration, Employee } from '../../feature-modules/data-models/dashboard-config.model';
import { Injectable } from '@angular/core';
import {
  CloneProject,
  DeleteProject,
  GetAllUsersProjects,
  GetDashboardConfiguration,
  GetUserProjects,
  ShareProject
} from './dashboard.actions';
import { DashboardService } from '../../feature-modules/dashboard/dashboard.service';
import { switchMap, tap } from 'rxjs';
import { WhitneyService } from '../../core/services/whitney.service';
import { UpdateProject } from '../project-details/project-details.actions';
import { VCPMService } from '../../core/services/vcpm.service';
import { VCPM } from 'pcs-domain';
import { SpinnerService } from '../../core/services/spinner/spinner.service';
import { LegacyCalculatorCheckService } from '../../core/services/legacy-calculator-check.service';
import { AppConstants } from '../../core/data-models/app-constants';

export interface DashboardStateModel {
  dashboardConfig: DashboardConfiguration;
  projects: Array<VCPM.Project>;
  allUserProjects: Array<VCPM.Project>;
  datagridPaginationMetadata: VCPM.DatagridPaginationMetadata;
  datagridPaginationAllUsersProjectsMetadata: VCPM.DatagridPaginationMetadata;
  employeeList: Array<Employee>;
}

@State<DashboardStateModel>({
  name: 'dashboard',
  defaults: {
    dashboardConfig: {} as DashboardConfiguration,
    projects: [],
    allUserProjects: [],
    datagridPaginationMetadata: {} as VCPM.DatagridPaginationMetadata,
    datagridPaginationAllUsersProjectsMetadata: {} as VCPM.DatagridPaginationMetadata,
    employeeList: []
  },
})
@Injectable()
export class DashboardState {
  constructor(private dashboardService: DashboardService, private vcpmService: VCPMService, private whitneyService: WhitneyService, private store: Store, private spinner: SpinnerService, private legacyCalculatorCheckService: LegacyCalculatorCheckService) {}

  @Selector()
  static getDashboardConfig(state: DashboardStateModel): DashboardConfiguration {
    if (!state) {
      throw Error('State is null or undefined');
    }
    return state?.dashboardConfig || {} as DashboardConfiguration;
  }

  @Selector()
  static getUserProjects(state: DashboardStateModel): Array<VCPM.Project> {
    return state?.projects;
  }
  @Selector()
  static getAllUsersProjects(state: DashboardStateModel): Array<VCPM.Project> {
    return state?.allUserProjects;
  }

  @Selector()
  static getDatagridPaginationMetadata(state: DashboardStateModel) {
    return state.datagridPaginationMetadata;
  }

  @Selector()
  static getEmployeeList(state: DashboardStateModel): Employee[] {
    return state.employeeList;
  }

  @Action(GetDashboardConfiguration)
  fetchDashboardConfiguration(ctx: StateContext<DashboardStateModel>) {
    this.spinner.show();
    ctx.patchState({ dashboardConfig: 
      {
        "enableGlobalSearch": true,
        "enablePagination": true,
        "pageSize": 10,
        "pageSizeOptions": [5,10,20,40,80,100],
        "enableEditMetadata": false,
        "columns": [
        {
          "name": "Project Name",
          "enableSortAndFilter": true,
          "filterField": "metadata.projectName",
          "customClass": "medium",
          "hidden": false
        },
        {
          "name": "Company Name",
          "enableSortAndFilter": true,
          "filterField": "metadata.companyName",
          "customClass": "medium",
          "hidden": false
        },
        {
          "name": "Tags",
          "enableSortAndFilter": true,
          "filterField": "metadata.tags",
          "customClass": "medium",
          "hidden": false
        },
        {
          "name": "Product",
          "enableSortAndFilter": true,
          "filterField": "metadata.product",
          "customClass": "medium",
          "hidden": false
        },
        {
          "name": "Product Version",
          "enableSortAndFilter": true,
          "filterField": "metadata.version",
          "customClass": "medium",
          "hidden": false
        },
        {
          "name": "Created Date",
          "enableSortAndFilter": true,
          "filterField": "createdDate",
          "customClass": "small",
          "hidden": false
        },
        {
          "name": "Updated Date",
          "enableSortAndFilter": true,
          "filterField": "lastUpdate",
          "customClass": "small",
          "hidden": false
        },
        {
          "name": "Description",
          "enableSortAndFilter": true,
          "filterField": "metadata.description",
          "customClass": "medium",
          "hidden": false
        },
        {
          "name": "Project Status",
          "enableSortAndFilter": true,
          "filterField": "metadata.status",
          "customClass": "medium",
          "hidden": false
        },
        {
          "name": "Actions",
          "enableSortAndFilter": false,
          "filterField": "",
          "customClass": "medium"
        }
        ],
        "allowedActions": {
        "add": true, 
        "edit": true,
        "delete": true,
        "clone": true,
        "share": true,
        "download": true
        }
      }
    });
  }

  @Action(GetUserProjects)
  fetchUserProjects(ctx: StateContext<DashboardStateModel>) {
    return this.vcpmService.fetchUserProjects().pipe(
      tap((response: VCPM.ProjectsApiResponse) => {
        ctx.patchState({
          projects: response !== null ? response?.content : [],
          datagridPaginationMetadata: response?.metadata,
        });
      })
    );
  }
   @Action(GetAllUsersProjects)
  fetchAllUsersProjects(ctx: StateContext<DashboardStateModel>) {
    this.spinner.show();
    return this.vcpmService.fetchAllUserProjects().pipe(
      tap((response: VCPM.ProjectsApiResponse) => {
        ctx.patchState({
          allUserProjects: response?.content,
          datagridPaginationAllUsersProjectsMetadata: response?.metadata,
        });
      })
    );
  }

  @Action(DeleteProject)
  deleteProject(ctx: StateContext<DashboardStateModel>, action: DeleteProject) {
    return this.vcpmService.deleteUserProject(action.projectId);
  }

  @Action(CloneProject)
  cloneProject(ctx: StateContext<DashboardStateModel>, action: CloneProject) {
    return this.vcpmService.cloneUserProject(action.projectId).pipe(
      switchMap((response: any) => {
        let metadata = response.metadata;
        metadata.projectName = this.dashboardService.getCopyName(action.projectId, action.isAdmin);

        const isLegacyProject = this.legacyCalculatorCheckService.isLegacyCalculatorProject({
          createdDate: action.originalCreatedDate
        } as VCPM.Project);

        if (isLegacyProject) {
          metadata.useNewCalculator = true;
          metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
          metadata.currentTab = AppConstants.wldDomainSizing;

          let projectData = response.data;
          if (projectData.output) {
            projectData.output = undefined;
          }

          let payload = {
            metadata: metadata,
            data: projectData,
            updateMinorVersion: true
          };
          return this.store.dispatch(new UpdateProject(response.projectId, payload));
        } else {
          let payload = {
            metadata: metadata,
            data: response.data,
            updateMinorVersion: true
          };
          return this.store.dispatch(new UpdateProject(response.projectId, payload));
        }
      }),
      tap(() => { this.spinner.hide(); })
    );
  }

  @Action(ShareProject)
  shareProject(ctx: StateContext<DashboardStateModel>, action: ShareProject) {
    return this.vcpmService.shareUserProject(action.projectId, action.payload);
  }
}
